import type React from "react"
import { useState, useEffect } from "react"
import { BiChevronRight, BiSearch, BiX } from "react-icons/bi"
import { FaEye, FaUsers } from "react-icons/fa6"
import { BiPhone } from "react-icons/bi"
import { IoChevronBack } from "react-icons/io5"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { getIssuesReports, updateIssueStatus } from "../../redux/thunk"
import { Issue } from "../../types/issues"

// Custom Button Component
const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  onClick,
  ...props
}: {
  children: React.ReactNode
  variant?: "default" | "ghost" | "destructive" | "secondary"
  size?: "default" | "sm"
  className?: string
  onClick?: () => void
  [key: string]: any
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    destructive: "bg-red-500 text-white hover:bg-red-600",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200",
  }

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 text-sm",
  }

  return (
    <button className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`} onClick={onClick} {...props}>
      {children}
    </button>
  )
}

// Custom Input Component
const Input = ({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${className}`}
    {...props}
  />
);

// Custom Card Components
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`rounded-lg border bg-card text-card-foreground shadow-sm ${className}`}>{children}</div>
}

const CardHeader = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>{children}</div>
}

const CardTitle = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <h3 className={`text-2xl font-semibold leading-none tracking-tight ${className}`}>{children}</h3>
}

const CardContent = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`p-6 pt-0 ${className}`}>{children}</div>
}

// Custom Badge Component
const Badge = ({
  children,
  variant = "default",
  className = "",
}: {
  children: React.ReactNode;
  variant?: "default" | "secondary" | "destructive";
  className?: string;
}) => {
  const variants = {
    default: "bg-primary text-primary-foreground",
    secondary: "bg-secondary text-secondary-foreground",
    destructive: "bg-[#EA1919] text-white",
  };

  return (
    <div
      className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${variants[variant]} ${className}`}
    >
      {children}
    </div>
  );
};

export default function IssuesPage() {
  const dispatch = useAppDispatch()
  const {
    issues,
    currentIssue,
    metrics,
    pagination,
    status,
    issueUpdateStatus,
    error,
  } = useAppSelector((state) => state.logistics)
  
  const [showIssueModal, setShowIssueModal] = useState(false)
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("All Statuses")
  const [currentPage, setCurrentPage] = useState(1)
  const [updateForm, setUpdateForm] = useState({
    status: "" as "Open" | "Pending" | "In Progress" | "Resolved" | "Closed" | "",
    notes: ""
  })

  // Fetch data on component mount
  useEffect(() => {
    dispatch(getIssuesReports({ page: currentPage, limit: 10 }))
  }, [dispatch, currentPage])

  // Handle search and filter changes
  useEffect(() => {
    const delayTimer = setTimeout(() => {
      dispatch(getIssuesReports({
        page: 1,
        limit: 10,
        status: statusFilter,
        search: searchTerm,
      }))
      setCurrentPage(1)
    }, 500) // Debounce search

    return () => clearTimeout(delayTimer)
  }, [searchTerm, statusFilter, dispatch])

  // Handle successful update operations
  useEffect(() => {
    if (issueUpdateStatus === "succeeded") {
      // Reset update status after a short delay
      const timer = setTimeout(() => {
        // This will be handled in the reducer reset if needed
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [issueUpdateStatus])

  const handleIssueDetails = (issue: Issue) => {
    setSelectedIssue(issue)
    setUpdateForm({
      status: issue.status || "Open",
      notes: ""
    })
    setShowIssueModal(true)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value)
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "Open":
        return "bg-red-100 text-red-800"
      case "In Progress":
      case "Pending":
        return "bg-yellow-100 text-yellow-800"
      case "Resolved":
      case "Closed":
        return "bg-green-100 text-green-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const getPriorityColor = (issueType: string) => {
    if (issueType === "High Priority") return "text-red-600"
    if (issueType === "Weather") return "text-blue-600"
    if (issueType === "Technical") return "text-purple-600"
    return "text-gray-600"
  }

  const handleUpdateFormChange = (field: "status" | "notes", value: string) => {
    setUpdateForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleCloseModal = () => {
    setShowIssueModal(false)
    setSelectedIssue(null)
    setUpdateForm({ status: "", notes: "" })
  }

  const handleUpdateIssue = async () => {
    const issueId = selectedIssue?.issueId || selectedIssue?._id;
    if (!issueId || !updateForm.status) {
      return;
    }

    try {
      const result = await dispatch(updateIssueStatus({
        issueId: issueId,
        data: {
          status: updateForm.status,
          notes: updateForm.notes || undefined
        }
      }));

      if (updateIssueStatus.fulfilled.match(result)) {
        setShowIssueModal(false);
        setSelectedIssue(null);
        setUpdateForm({ status: "", notes: "" });
        
        // Refresh the issues list to get updated data
        dispatch(getIssuesReports({ 
          page: currentPage, 
          limit: 10, 
          status: statusFilter !== "All Statuses" ? statusFilter : undefined,
          search: searchTerm || undefined
        }));
      }
    } catch (error) {
      console.error('Error during update:', error);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button className="text-gray-400 hover:text-gray-600 transition-colors">
              <IoChevronBack className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm text-gray-500">Logistics</span>
                <BiChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500">Issues & Communication</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Issues</h1>
              <p className="text-sm text-gray-600 mt-1">Monitor and manage delivery issues and complaints</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Issues Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="flex flex-col border-l-4 border-l-green-600">
            <CardHeader className="flex flex-row items-start space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Open Issues</CardTitle>
            </CardHeader>
            <CardContent className="mt-auto">
              <div className="text-2xl font-bold">
                {status === "loading" ? "..." : metrics?.openIssues ?? 0}
              </div>
            </CardContent>
          </Card>

          <Card className="flex flex-col border-l-4 border-l-yellow-500">
            <CardHeader className="flex flex-row items-start space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">In Progress</CardTitle>
            </CardHeader>
            <CardContent className="mt-auto">
              <div className="text-2xl font-bold">
                {status === "loading" ? "..." : metrics?.inProgressIssues ?? 0}
              </div>
            </CardContent>
          </Card>

          <Card className="flex flex-col border-l-4 border-l-green-600">
            <CardHeader className="flex flex-row items-start space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Resolved Today</CardTitle>
            </CardHeader>
            <CardContent className="mt-auto">
              <div className="text-2xl font-bold">
                {status === "loading" ? "..." : metrics?.resolvedToday ?? 0}
              </div>
            </CardContent>
          </Card>

          <Card className="flex flex-col border-l-4 border-l-red-500">
            <CardHeader className="flex flex-row items-start space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">High Priority</CardTitle>
            </CardHeader>
            <CardContent className="mt-auto">
              <div className="text-2xl font-bold">
                {status === "loading" ? "..." : metrics?.highPriorityIssues ?? 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex items-center gap-x-6 justify-between mb-6">
          <div className="relative w-full">
            <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input 
              placeholder="Search Issues by rider name or order ID" 
              className="pl-10 h-10 w-[700px]" 
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="flex items-center gap-2">
            <select 
              className="h-10 rounded-md border border-input px-3 py-2 text-sm"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="All Statuses">All Statuses</option>
              <option value="Open">Open</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
        </div>

        {/* Issues List */}
        <div className="space-y-4">
          {status === "loading" && (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading issues...</p>
            </div>
          )}
          
          {status === "failed" && (
            <div className="text-center py-8">
              <p className="text-red-500">Error loading issues: {error}</p>
            </div>
          )}
          
          {status === "succeeded" && issues.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No issues found</p>
            </div>
          )}
          
          {status === "succeeded" && issues.length > 0 && (
            issues.map((issue, index) => (
              <Card key={issue.issueId || issue._id || index}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{issue.orderId}</span>
                        <span className={getPriorityColor(issue.issuesType)}>•</span>
                        <span className="text-sm text-gray-600">{issue.issuesType}</span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {issue.customerName ? `${issue.customerName} (Customer)` : "Rider"} • {issue.issuesType || "General Issue"}
                      </p>
                      <p className="text-xs text-green-600">
                        {issue.createdAt ? new Date(issue.createdAt).toLocaleDateString() : "N/A"}
                      </p>
                    </div>
                    <div className="flex items-center gap-x-10">
                      <Badge className={getStatusBadgeClass(issue.status)}>
                        {issue.status}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-green-600 rounded-lg border border-[#000000BF] text-[#0000008C] space-x-2"
                        onClick={() => handleIssueDetails(issue)}
                      >
                        <span>View</span> <FaEye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          
          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || status === "loading"}
              >
                Previous
              </Button>
              
              <span className="text-sm text-gray-600">
                Page {currentPage} of {pagination.totalPages} ({pagination.totalReports} total issues)
              </span>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                disabled={currentPage === pagination.totalPages || status === "loading"}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Issue Details Modal */}
      {showIssueModal && selectedIssue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-green-600">Issue Details - {selectedIssue.orderId}</h3>
                <p className="text-sm text-gray-500">
                  View and manage issue {selectedIssue.orderId} {selectedIssue.customerName ? `reported by ${selectedIssue.customerName}` : "reported by rider"}.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleCloseModal}>
                <BiX className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-medium">{selectedIssue.issuesType} Issue</h4>
                <div className="flex items-center gap-2 mt-1">
                  <FaUsers className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{selectedIssue.issuesType}</span>
                  <Badge className={getStatusBadgeClass(selectedIssue.status)}>
                    {selectedIssue.status}
                  </Badge>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-2">Description</h5>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    {selectedIssue.description || "No description provided"}
                  </p>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-3">Issue Information:</h5>
                <div className="space-y-2">
                  {selectedIssue.customerName && (
                    <div className="flex items-center gap-2">
                      <FaUsers className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{selectedIssue.customerName} (Customer)</span>
                    </div>
                  )}
                  {selectedIssue.logisticId && (
                    <div className="flex items-center gap-2">
                      <BiPhone className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">Rider ID: {selectedIssue.logisticId}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-green-600">Order:</span>
                    <span className="text-sm">{selectedIssue.orderId}</span>
                  </div>
                  {selectedIssue.createdAt && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-green-600">Created:</span>
                      <span className="text-sm">
                        {new Date(selectedIssue.createdAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {selectedIssue.notes && (
                <div>
                  <h5 className="font-medium mb-2">Existing Notes:</h5>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-700">{selectedIssue.notes}</p>
                  </div>
                </div>
              )}

              {(selectedIssue.issueId || selectedIssue._id) ? (
                <div>
                  <h5 className="font-medium mb-3">Add a Response:</h5>
                  <textarea
                    className="w-full p-3 border border-gray-300 rounded-md h-24 resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Type your response and update (optional)"
                    value={updateForm.notes}
                    onChange={(e) => handleUpdateFormChange("notes", e.target.value)}
                  />
                  <div className="flex items-center justify-between mt-3">
                    <select 
                      className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      value={updateForm.status}
                      onChange={(e) => handleUpdateFormChange("status", e.target.value as any)}
                    >
                      <option value="">Select Status</option>
                      <option value="Open">Open</option>
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Resolved">Resolved</option>
                      <option value="Closed">Closed</option>
                    </select>
                    <Button 
                      className="bg-green-600 text-white hover:bg-green-700"
                      onClick={handleUpdateIssue}
                      disabled={!updateForm.status || issueUpdateStatus === "loading"}
                    >
                      {issueUpdateStatus === "loading" ? "Updating..." : "Update"}
                    </Button>
                  </div>
                  {!updateForm.status && (
                    <p className="text-xs text-red-500 mt-1">Please select a status to update</p>
                  )}
                </div>
              ) : (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h5 className="font-medium mb-2 text-yellow-800">Issue Information Only</h5>
                  <p className="text-sm text-yellow-700">
                    This issue cannot be updated through the admin panel as it doesn't have a valid issue ID. 
                    Please contact the system administrator or try refreshing the data.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}